import { useListCamerasQuery } from "@/core/cameraApi";
import { useCameraApiStatus, useCameraApiUrl } from "@/hooks";
import { cn } from "@/lib/utils";
import {
  useCameraApiRetry,
  useCameraStreamRecovery,
} from "@/modules/camera-stream";
import {
  SafeZoneOverlay,
  ZoneConfig,
} from "@/modules/dashboard/components/DashboardZoneConfiguration";
import { useRelayReconnect } from "@/modules/device";
import { EditProjectModal } from "@/modules/project/components/EditProjectModal";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import {
  LocalAccessDisabled,
  NoCameraConfigured,
  NoCameraDetected,
  RelayUnavailable,
  SearchingForCamera,
} from "@/modules/ui";
import { useState } from "react";
import { useParams } from "react-router";
import { LiveInference } from "../LiveInference/LiveInference";

/** Every state the preview box can be in; only "live" shows a stream. */
type PreviewState =
  | "searching"
  | "undetected"
  | "unconfigured"
  | "relay-unavailable"
  | "local-access-revoked"
  | "live";

interface CameraPreviewProps {
  zoneConfig: ZoneConfig;
  selectedCamera: string | null;
  selectedStream: string | null;
}

export const CameraPreview = ({
  zoneConfig,
  selectedCamera,
  selectedStream,
}: CameraPreviewProps) => {
  const [activeProject] = useActiveProject();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const isSwitchingProject =
    !activeProject || String(activeProject.id) !== urlProjectId;
  const cameraApiUrl = useCameraApiUrl();
  const cameraApiStatus = useCameraApiStatus();
  const { reconnect, isReconnecting } = useRelayReconnect();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const {
    data: cameras,
    isLoading: camerasLoading,
    isFetching: camerasFetching,
    isError: camerasError,
  } = useListCamerasQuery(undefined, {
    skip: !cameraApiUrl || isSwitchingProject,
  });
  const retryCameraApi = useCameraApiRetry();

  // isError as well as an empty list — RTK Query keeps the last successful
  // body on a rejected refetch, so `cameras` alone would lie about a
  // controller that has since gone away. A skipped query (no camera API URL)
  // lands here too, matching Capture's !cameraApiUrl branch.
  const camerasUnavailable = camerasError || !cameras || cameras.length === 0;
  // Controller is up but no longer reports the project's camera.
  const selectedCameraMissing =
    !camerasUnavailable &&
    !!selectedCamera &&
    !cameras.some((camera) => camera.mxid === selectedCamera);
  const cameraUnreachable = camerasUnavailable || selectedCameraMissing;

  // Capture owns this on its own page; Run needs it too, or Refresh restores
  // the camera list while the stream stays dead.
  useCameraStreamRecovery({
    isCameraAvailable: !!selectedCamera && !cameraUnreachable,
    isLoading: camerasLoading,
  });

  const openSettings = activeProject
    ? () => setSettingsOpen(true)
    : undefined;

  // Same ladder as CapturePage, scoped to the preview box: every state the
  // stream can't be shown in gets the screen that says why, rather than
  // something that reads as a working preview.
  const state = ((): PreviewState => {
    if (isSwitchingProject) return "searching";

    // The camera API's own state comes first. While it is connecting or
    // unreachable the cameras query is skipped, which lands in
    // `camerasUnavailable` below and would blame the camera for the relay.
    if (cameraApiStatus.kind === "connecting") return "searching";
    if (cameraApiStatus.kind === "relay-unavailable") return "relay-unavailable";
    if (cameraApiStatus.kind === "local-access-revoked") return "local-access-revoked";

    if (camerasLoading) return "searching";
    if (camerasUnavailable || selectedCameraMissing) return "undetected";
    // Cameras detected but the project has no camera set — deliberately no
    // fallback: the camera is picked explicitly in project settings.
    if (!selectedCamera) return "unconfigured";
    // Stream auto-pick still pending, see useSelectedCameraStream.
    if (!selectedStream) return "searching";

    return "live";
  })();

  const isLive = state === "live";

  return (
    <>
      {/* The tint stands in for a video frame, and the zone overlay only means
          something over one — both are for the live state only; the rest are
          messages that sit on the page background like Capture's do. */}
      <div
        className={cn(
          "relative aspect-video w-full max-w-full overflow-hidden rounded-xl",
          isLive && "bg-black/5",
        )}
      >
        <div className="absolute inset-0">
          {state === "searching" && (
            <SearchingForCamera
              compact
              url={isSwitchingProject ? undefined : cameraApiUrl}
            />
          )}
          {state === "undetected" && (
            <NoCameraDetected
              compact
              onRefresh={retryCameraApi}
              isRefreshing={camerasFetching}
              onOpenSettings={openSettings}
            />
          )}
          {state === "unconfigured" && (
            <NoCameraConfigured compact onOpenSettings={openSettings} />
          )}
          {state === "relay-unavailable" && (
            <RelayUnavailable
              compact
              message={
                cameraApiStatus.kind === "relay-unavailable"
                  ? cameraApiStatus.message
                  : ""
              }
              onRetry={reconnect}
              isRetrying={isReconnecting}
            />
          )}
          {state === "local-access-revoked" && (
            <LocalAccessDisabled
              compact
              cameraApiUrl={
                cameraApiStatus.kind === "local-access-revoked"
                  ? cameraApiStatus.cameraApiUrl
                  : ""
              }
              onOpenSettings={openSettings}
            />
          )}
          {isLive && (
            <LiveInference
              selectedCamera={selectedCamera!}
              selectedStream={selectedStream!}
            />
          )}
        </div>

        {isLive && (
          <SafeZoneOverlay
            direction={zoneConfig.zoneDirection}
            centerPct={zoneConfig.zoneCenter}
            thicknessPct={zoneConfig.zoneThickness}
          />
        )}
      </div>

      {settingsOpen && activeProject && (
        <EditProjectModal
          project={activeProject}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );
};
